import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { User, CreateUserRequest, UpdateUserRequest, UserRole } from '../types';
import authService from '../services/authService';
import {
  Button, Badge, PageHeader, Modal, Field, Input, Select, FormActions,
  LoadingScreen, EmptyState, RowAction, Icon, tableCard, tableScroll, th, td, tableRow,
} from '../components/ui';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    role: UserRole.USER as UserRole,
    active: true
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const userList = await authService.getAllUsers();
      setUsers(userList);
    } catch (error) {
      toast.error('Benutzer konnten nicht geladen werden');
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingUser) {
        const updateData: UpdateUserRequest = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          role: formData.role,
          active: formData.active,
          ...(formData.password && { password: formData.password })
        };

        await authService.updateUser(editingUser.id, updateData);
        toast.success('Benutzer gespeichert');
        setEditingUser(null);
      } else {
        const createData: CreateUserRequest = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          password: formData.password,
          role: formData.role
        };

        await authService.createUser(createData);
        toast.success('Benutzer angelegt');
        setShowCreateForm(false);
      }

      setFormData({ firstName: '', lastName: '', username: '', password: '', role: UserRole.USER, active: true });
      await loadUsers();
    } catch (error: any) {
      toast.error(error.message || 'Benutzer konnte nicht gespeichert werden');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      password: '',
      role: user.role,
      active: user.active
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (user: User) => {
    if (window.confirm(`Benutzer „${user.firstName} ${user.lastName}“ löschen?\nEr kann sich danach nicht mehr anmelden.`)) {
      try {
        await authService.deleteUser(user.id);
        toast.success('Benutzer gelöscht');
        await loadUsers();
      } catch (error: any) {
        toast.error(error.message || 'Benutzer konnte nicht gelöscht werden');
      }
    }
  };

  const resetForm = () => {
    setFormData({ firstName: '', lastName: '', username: '', password: '', role: UserRole.USER, active: true });
    setEditingUser(null);
    setShowCreateForm(false);
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <PageHeader
        title="Benutzerverwaltung"
        subtitle="Mitarbeiterkonten und Berechtigungen verwalten."
        actions={<Button onClick={() => setShowCreateForm(true)} icon={<Icon.Plus className="h-4 w-4" />}>Benutzer anlegen</Button>}
      />

      {showCreateForm && (
        <Modal title={editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'} onClose={resetForm} maxWidth="max-w-md">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Vorname" required><Input required value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} /></Field>
              <Field label="Nachname" required><Input required value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} /></Field>
            </div>
            <Field label="Benutzername" required><Input required value={formData.username} onChange={e => setFormData({ ...formData, username: e.target.value })} /></Field>
            <Field label={`Passwort${editingUser ? ' (leer lassen, um es beizubehalten)' : ''}`} required={!editingUser}>
              <Input type="password" required={!editingUser} value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} />
            </Field>
            <Field label="Rolle">
              <Select value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value as UserRole })}>
                <option value={UserRole.USER}>Benutzer</option>
                <option value={UserRole.ADMIN}>Administrator</option>
              </Select>
            </Field>
            {editingUser && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={formData.active} onChange={e => setFormData({ ...formData, active: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                Aktiv
              </label>
            )}
            <FormActions>
              <Button type="button" variant="secondary" onClick={resetForm}>Abbrechen</Button>
              <Button type="submit">{editingUser ? 'Speichern' : 'Anlegen'}</Button>
            </FormActions>
          </form>
        </Modal>
      )}

      <div className={tableCard}>
        <div className={tableScroll}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/80">
              <tr>
                <th className={th}>Name</th>
                <th className={`${th} hidden lg:table-cell`}>Benutzername</th>
                <th className={th}>Rolle</th>
                <th className={th}>Status</th>
                <th className={`${th} hidden lg:table-cell`}>Erstellt</th>
                <th className={`${th} text-right`}>Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(user => (
                <tr key={user.id} className={tableRow}>
                  <td className={`${td} whitespace-nowrap`}>
                    <div className="flex items-center gap-3">
                      <div className="grid h-9 w-9 place-items-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700">
                        {user.firstName[0]}{user.lastName[0]}
                      </div>
                      <span className="font-medium text-gray-900">{user.firstName} {user.lastName}</span>
                    </div>
                  </td>
                  <td className={`${td} whitespace-nowrap text-gray-700 hidden lg:table-cell`}>{user.username}</td>
                  <td className={`${td} whitespace-nowrap`}>
                    <Badge className={user.role === UserRole.ADMIN ? 'bg-brand-100 text-brand-800' : 'bg-gray-100 text-gray-700'}>
                      {user.role === UserRole.ADMIN ? 'Administrator' : 'Benutzer'}
                    </Badge>
                  </td>
                  <td className={`${td} whitespace-nowrap`}>
                    <Badge className={user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {user.active ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </td>
                  <td className={`${td} whitespace-nowrap text-gray-500 hidden lg:table-cell`}>
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('de-DE') : '–'}
                  </td>
                  <td className={`${td} whitespace-nowrap text-right`}>
                    <div className="flex items-center justify-end gap-1">
                      <RowAction onClick={() => handleEdit(user)}>Bearbeiten</RowAction>
                      <RowAction danger onClick={() => handleDelete(user)}>Löschen</RowAction>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {users.length === 0 && (
          <EmptyState
            title="Noch keine Benutzer"
            hint="Jeder Mitarbeiter braucht ein eigenes Konto, um sich anzumelden."
            action={<Button onClick={() => setShowCreateForm(true)} icon={<Icon.Plus className="h-4 w-4" />}>Benutzer anlegen</Button>}
          />
        )}
      </div>
    </div>
  );
};

export default Users;
